import {
  forwardRef,
  useEffect,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { AnimatePresence, m } from 'motion/react';
import { AlertCircle } from 'lucide-react';
import { cx } from '@/lib/cx';
import { formatDecimal, parseDecimalInput, type Decimal } from '@/lib/format/decimal';

export const inputBase =
  'w-full bg-surface-0 text-ink-900 placeholder:text-ink-300 border border-line-strong rounded-(--radius-input) ' +
  'transition-[border-color,box-shadow,background-color] duration-150 ' +
  'hover:border-ink-300 focus:border-gold-500 focus:shadow-focus focus:outline-none ' +
  'disabled:bg-surface-2 disabled:text-ink-500 aria-invalid:border-danger-600 aria-invalid:focus:shadow-[0_0_0_3px_rgb(179_38_30/0.18)]';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
  aside?: ReactNode;
}

export function Field({ label, hint, error, required, className, children, aside }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
      {(label || aside) && (
        <div className="flex items-baseline justify-between gap-2">
          {label && (
            <label htmlFor={id} className="text-[13px] font-medium text-ink-700">
              {label}
              {required && <span className="ml-0.5 text-gold-600">*</span>}
            </label>
          )}
          {aside}
        </div>
      )}
      {children({
        id,
        'aria-describedby': error || hint ? hintId : undefined,
        'aria-invalid': error ? true : undefined,
      })}
      <AnimatePresence initial={false} mode="wait">
        {error ? (
          <m.p
            key="error"
            id={hintId}
            initial={{ opacity: 0, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-1 text-[12.5px] text-danger-600"
          >
            <AlertCircle className="mt-[2px] size-3.5 shrink-0" aria-hidden />
            {error}
          </m.p>
        ) : hint ? (
          <m.p key="hint" id={hintId} initial={false} className="text-[12.5px] text-ink-500">
            {hint}
          </m.p>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { leading?: ReactNode }>(
  function TextInput({ className, leading, ...rest }, ref) {
    if (leading)
      return (
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">{leading}</span>
          <input ref={ref} className={cx(inputBase, 'h-10 pr-3 pl-9', className)} {...rest} />
        </div>
      );
    return <input ref={ref} className={cx(inputBase, 'h-10 px-3', className)} {...rest} />;
  },
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextArea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx(inputBase, 'min-h-20 px-3 py-2 leading-relaxed', className)} {...rest} />;
  },
);

export interface DecimalInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'defaultValue' | 'prefix'> {
  value: Decimal | null | undefined;
  onChange: (value: Decimal | null) => void;
  /** Fraction digits shown when not focused. */
  digits?: { min?: number; max?: number };
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Measures read "120.5" as a decimal; amounts read "1.565" as thousands. */
  measure?: boolean;
  inputClassName?: string;
}

/**
 * Number entry in Argentine format. Shows "1.565,50" at rest, lets the person type freely while focused, and emits
 * the API's dot decimal string (never a JS number). Invalid text keeps the last valid value and marks the field.
 */
export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(function DecimalInput(
  { value, onChange, digits, prefix, suffix, measure, className, inputClassName, onBlur, onFocus, ...rest },
  ref,
) {
  const display = (v: Decimal | null | undefined) =>
    v === null || v === undefined || v === '' ? '' : formatDecimal(v, { min: digits?.min ?? 0, max: digits?.max ?? 3 });
  const [text, setText] = useState(() => display(value));
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(display(value));
      setInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  return (
    <div className={cx('relative flex items-center', className)}>
      {prefix && (
        <span className="pointer-events-none absolute left-3 text-[13px] font-medium text-ink-400">{prefix}</span>
      )}
      <input
        ref={ref}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={text}
        aria-invalid={rest['aria-invalid'] || invalid || undefined}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
          // select() takes focus in Chromium: only do it if the person is still on this field a frame later.
          const field = e.target;
          requestAnimationFrame(() => {
            if (document.activeElement === field) field.select();
          });
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === '') {
            setInvalid(false);
            onChange(null);
            return;
          }
          const parsed = parseDecimalInput(next, { dotThousands: !measure });
          setInvalid(parsed === null);
          if (parsed !== null) onChange(parsed);
        }}
        className={cx(
          inputBase,
          'tabular h-10 text-right',
          prefix ? 'pl-11' : 'pl-3',
          suffix ? 'pr-12' : 'pr-3',
          inputClassName,
        )}
        {...rest}
      />
      {suffix && <span className="pointer-events-none absolute right-3 text-[12.5px] text-ink-400">{suffix}</span>}
    </div>
  );
});
